<template>
  <tabs class="!py-5">
    <template #actions>
      <Switch
        v-model="hideDeleted"
        :label="$t('hideDeletedDrivers')"
      />
    </template>
  </tabs>
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filterParams" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/drivers/companyMode/csv"
      :filter="providerParams.filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <data-table
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :row-link="(driver) => driver.user ? ({ name: 'driver-details', params: { driverId: driver.user.driver.id } }) : ({ name: 'drivers' })"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :disableFrontendSorting="true"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, ref, unref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {
    collOptionsProvider,
    enumOptionsProvider,
    useProvider,
    withExtraOptions,
} from '@components/utils/table/hooks/provider';
import {format} from 'date-fns';
import type {Filter, ProviderParams} from '@/components/utils/table/hooks/provider';
import type {
  Address,
  Company,
  Driver,
  HydraCollection,
  License, LicenseSeat,
  Region,
  Tour
} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import {useQueryRef} from '@/hooks/url';
import {formatNumberForCurrency, formatTimeDurationFromSeconds} from '@/utils/helpers';
import Switch from '@utils/inputs/Switch.vue';
import {useTableFields} from '@hooks/table-configuration';
import { useRoute } from 'vue-router';
import globalLicenseFilterKey from '@/components/utils/globalLicenseFilter/injects';
import { fetchApi } from '@/hooks/fetch';

interface Props {
  modelValue: Company | null;
}

const props = defineProps<Props>();

const hideDeleted = ref(true);

const route = useRoute();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const globalFilter = inject(globalLicenseFilterKey)!

const filterLicense = ref<License>();
watch(() => globalFilter.value.licenseId, async () => {
  if(!globalFilter.value.licenseId) {
    filterLicense.value = undefined;
    return;
  }

  filterLicense.value = await fetchApi<License>(globalFilter.value.licenseId);
}, {immediate: true});
const csvTitle = computed(() => {
  let title = t('csv.title.driversFromCompany', {
    company: props.modelValue?.name,
  });

  if(filterLicense.value) {
    title += `_${filterLicense.value?.startDate ? format(new Date(filterLicense.value.startDate), 'dd-MM-yyyy') : ''}_${filterLicense.value?.endDate ? format(new Date(filterLicense.value.endDate), 'dd-MM-yyyy') : ''}`;
  }

  if(globalFilter.value?.boxType) {
    title += `_${t('licenses.boxType.'+globalFilter.value.boxType)}`;
  }

  return title;
});

const sortParams = reactive({
  sortKey,
  sortDirection,
})
const filterParams = ref<Filter>(filter.value)
const paginationParams = reactive({
  page,
  perPage,
})
const providerParams = computed<ProviderParams>(() => {
  const filter: Filter = {
    ...filterParams.value,
    'licenseSeat.license.company': `/companies/${route.params.companyId}`,
  }

  if(globalFilter.value?.licenseId) {
    filter['licenseSeat.license'] = globalFilter.value.licenseId
  }

  if(globalFilter.value?.boxType) {
    filter['licenseSeat.type'] = globalFilter.value.boxType
  }

  if (hideDeleted.value) {
    filter['user.deleted'] = false
  }
  return {
    sort: sortParams,
    filter,
    pagination: paginationParams,
  }
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driverList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    sortable: true,
  },
  {
    key: 'user.salutation',
    csvTranslations: {
      male: t('title.male'),
      female: t('title.female'),
      other: t('title.other'),
      unknown: t('title.unknown'),
    },
    title: t('driver.title'),
    group: t('pages.driverList.fieldGroups.general'),
    formatter: (v: string) => t(`title.${v ?? 'unknown'}`),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/salutation', (v) => ({
          label: t(`title.${v}`),
          value: v,
        })),
        label: t('driver.title'),
      },
    },
  },
  {
    key: 'user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.driverList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'user.lastName',
    title: t('user.lastName'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'user.email',
    title: t('user.email'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'user.phoneNumber',
    title: t('user.phoneNumber'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.phoneNumber'),
      },
    },
  },
  {
    key: 'user.address.street',
    title: t('address.street'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('address.street'),
      },
    },
  },
  {
    key: 'user.address.cityName',
    title: t('address.city'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('address.city'),
      },
    },
  },
  {
    key: 'user.address.zip',
    title: t('address.zip'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('address.zip'),
      },
    },
  },
  {
    key: 'bicycleType',
    title: t('driver.bicycleType'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    csvTranslations: {
      null: t('driver.bicycleTypes.null'),
      RB: t('driver.bicycleTypes.RB'),
      MTB: t('driver.bicycleTypes.MTB'),
      EMTB: t('driver.bicycleTypes.EMTB'),
      CB: t('driver.bicycleTypes.CB'),
      ECB: t('driver.bicycleTypes.ECB'),
      SS: t('driver.bicycleTypes.SS'),
    },
    formatter: (v: string) => v ? t(`driver.bicycleTypes.${v}`) : t('driver.bicycleTypes.null'),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/Bicycle',(v) => ({
          value: v,
          label: t(`driver.bicycleTypes.${v}`),
        })),
        label: t('driver.bicycleType'),
      },
    },
  },
  {
    key: 'driverType',
    title: t('driver.driverType.DEFAULT'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string|null) => v ? t(`driver.driverType.${v}`) : '',
    csvTranslations: {
      'none': t('driver.driverType.none'),
      'private': t('driver.driverType.private'),
      'business': t('driver.driverType.business'),
      'mixed': t('driver.driverType.mixed'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.driverType.DEFAULT'),
        options: enumOptionsProvider('/enums/DriverType', (v) => ({
          value: v,
          label: t(`driver.driverType.${v}`),
        })),
      },
    },
  },
  {
    key: 'driverTypeAtRegistration',
    title: t('driver.driverTypeAtRegistration'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string|null) => v ? t(`driver.driverType.${v}`) : '',
    csvTranslations: {
      'none': t('driver.driverType.none'),
      'private': t('driver.driverType.private'),
      'business': t('driver.driverType.business'),
      'mixed': t('driver.driverType.mixed'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.driverTypeAtRegistration'),
        options: enumOptionsProvider('/enums/DriverType', (v) => ({
          value: v,
          label: t(`driver.driverType.${v}`),
        })),
      },
    },
  },
  /**
   * ------------------------------------------------------------------------------
   */
  {
    key: 'license',
    title: t('driver.license'),
    sortable: false,
    visible: true,
    group: t('pages.driverList.fieldGroups.license'),
    computed: (driver: Driver) =>
      `${driver.licenseSeat?.license?.company?.name} - ${driver.licenseSeat?.license?.startDate ?
        format(new Date(driver.licenseSeat?.license?.startDate),
          'dd.MM.yyyy') : ''} - ${driver.licenseSeat?.license?.endDate ?
        format(new Date(driver.licenseSeat?.license?.endDate), 'dd.MM.yyyy') : ''}`,
  },
  {
    key: 'licenseSeat.type',
    title: t('pages.driverList.fields.licenseType'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`licenses.boxType.${v}`),
    csvTranslations: {
      box: t('licenses.boxType.box'),
      noBox: t('licenses.boxType.noBox'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        options: enumOptionsProvider('/enums/LicenseType', (v) => ({
          value: v,
          label: t(`licenses.boxType.${v}`),
        })),
        label: t('pages.driverList.fields.licenseType'),
      },
    },
  },
  {
    key: 'companyProfileComplete',
    title: t('driver.companyProfileComplete'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string | null) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('driver.companyProfileComplete'),
      },
    },
  },
  {
    key: 'canton',
    title: t('driver.canton'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`canton.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        options: enumOptionsProvider('/enums/Canton', (v) => ({
          value: v,
          label: v ? t(`canton.${v}`) : '-',
        })),
        label: t('driver.canton'),
      },
    },
  },
  {
    key: 'saddlebags',
    title: t('driver.saddlebags'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v?: boolean|null) => yesNoNull(v),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('null'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['saddlebags'];
            m['exists[saddlebags]'] = false;
          } else {
            delete m['exists[saddlebags]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[saddlebags]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: t('driver.saddlebags'),
      },
    },
  },
  {
    key: 'luggageRackInBack',
    title: t('driver.luggageRackInBack'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v?: boolean|null) => yesNoNull(v),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('null'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['luggageRackInBack'];
            m['exists[luggageRackInBack]'] = false;
          } else {
            delete m['exists[luggageRackInBack]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[luggageRackInBack]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: t('driver.luggageRackInBack'),
      },
    },
  },
  {
    key: 'luggageRackUsable',
    title: t('driver.luggageRackUsable'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v?: boolean|null) => yesNoNull(v),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('null'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['luggageRackUsable'];
            m['exists[luggageRackUsable]'] = false;
          } else {
            delete m['exists[luggageRackUsable]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[luggageRackUsable]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: t('driver.luggageRackUsable'),
      },
    },
  },
  {
    key: 'batteryPosition',
    title: t('driver.batteryPosition.DEFAULT'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? t(`driver.batteryPosition.${v}`) : '-',
    csvTranslations: {
      belowLuggageRack: t('driver.batteryPosition.belowLuggageRack'),
      inFrame: t('driver.batteryPosition.inFrame'),
      other: t('driver.batteryPosition.other'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        options: enumOptionsProvider('/enums/BatteryPosition', (v) => ({
          value: v,
          label: v ? t(`driver.batteryPosition.${v}`) : '-',
        })),
        label: t('driver.batteryPosition.DEFAULT'),
      },
    },
  },
  {
    key: 'anonymousRanking',
    title: t('driver.anonymousRanking'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v?: boolean|null) => yesNoNull(v),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('null'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['anonymousRanking'];
            m['exists[anonymousRanking]'] = false;
          } else {
            delete m['exists[anonymousRanking]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[anonymousRanking]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: t('driver.anonymousRanking'),
      },
    },
  },
  {
    key: 'licenseSeat.license.startDate',
    title: t('licenses.startDate'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('licenses.startDate'),
      },
    },
  },
  {
    key: 'licenseSeat.license.endDate',
    title: t('licenses.endDate'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('licenses.endDate'),
      },
    },
  },
  {
    key: 'lastVerification',
    title: t('driver.lastVerification'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('driver.lastVerification'),
      },
    },
  },
  {
    key: 'verificationRequested',
    title: t('driver.verificationRequested'),
    group: t('pages.driverList.fieldGroups.license'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.license'),
      options: {
        label: t('driver.verificationRequested'),
      },
    },
  },
  /**
   * ------------------------------------------------------------------------------
   */
  {
    key: 'createdAt',
    title: t('createdAt'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('createdAt'),
      },
    },
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
  {
    key: 'totalDrivenDistance',
    title: t('driver.totalDistance'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    // filter: {
    //   type: 'range',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     icon: <span>km</span>,
    //     label: t('driver.totalDistance'),
    //     transformOut: (v: number) => v * 1000,
    //     transformIn: (v: number) => v / 1000,
    //   },
    // },
  },
  {
    key: 'totalDrivenTime',
    title: t('driver.totalTime') + ' (HH:MM:SS)',
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: number) => `${formatTimeDurationFromSeconds(v)}`,
    // filter: {
    //   type: 'duration',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     label: t('driver.totalTime'),
    //     placeholderFrom: 'hh:mm:ss',
    //     placeholderTo: 'hh:mm:ss',
    //   },
    // },
  },
  {
    key: 'licenseProgress',
    title: t('driver.licenseProgress'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: number) => `${v.toFixed(2).padStart(5, '0')}%`,
  },
  {
    key: 'kcalSpent',
    title: t('driver.kcal'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    unit: 'kcal',
    // filter: {
    //   type: 'range',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     icon: <span>kcal</span>,
    //     label: t('driver.kcal'),
    //   },
    // },
  },
  {
    key: 'co2Saved',
    title: t('driver.co2'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    unit: 'kg',
    // filter: {
    //   type: 'range',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     icon: <span>co2</span>,
    //     label: t('driver.co2'),
    //   },
    // },
  },
  {
    key: 'avgSpeed',
    title: t('driver.averageSpeed'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    unit: 'km/h',
    formatter: (v: number) => v ? `${(v*3.6).toFixed(2)} km/h` : '0 km/h',
    // filter: {
    //   type: 'range',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     icon: <span>km/h</span>,
    //     label: t('driver.averageSpeed'),
    //     transformOut: (v: number) => v/3.6,
    //     transformIn: (v: number) => v*3.6,
    //   },
    // },
  },
  {
    key: 'numOfTours',
    title: t('driver.numOfTours'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    // filter: {
    //   type: 'range',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     label: t('driver.numOfTours'),
    //   },
    // },
  },
  {
    key: 'numOfDaysWithTour',
    title: t('driver.numOfDaysWithTour'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    // filter: {
    //   type: 'range',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     label: t('driver.numOfDaysWithTour'),
    //   },
    // },
  },
  {
    key: 'timestampLastRide',
    title: t('driver.lastRide'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    // filter: {
    //   type: 'datetime',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     label: t('driver.lastRide'),
    //   },
    // },
  },
  {
    key: 'numOfTopRatedPhotos',
    title: t('driver.numOfTopRatedPhotos'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: false,
    // filter: {
    //   type: 'range',
    //   group: t('pages.driverList.filterGroups.calculations'),
    //   options: {
    //     label: t('driver.numOfTopRatedPhotos'),
    //   },
    // },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const { data: items, loading } = useProvider<HydraCollection<Driver[]>>('/drivers/companyMode', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const yesNoNull = (v?: boolean|null) => {
  if (v === undefined || v === null) return '';
  return v ? t('yes') : t('no');
};
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
